import { useEffect } from 'react';

import { sendErrorEvent } from '@trello/error-reporting';
import { featureFlagClient } from '@trello/feature-flag-client';
import type {
  BoardSubscription,
  EnterpriseSubscription,
  MemberSubscription,
  OrganizationSubscription,
} from '@trello/realtime-updater';
import { Tags } from '@trello/realtime-updater';

import { subscriber } from 'app/scripts/init/subscriber';

type ModelType = 'Member' | 'Board' | 'Organization' | 'Enterprise';
type SubscriptionType =
  | MemberSubscription
  | BoardSubscription
  | OrganizationSubscription
  | EnterpriseSubscription;

export const isMongoId = (id?: string): boolean => {
  return !!id && /^[0-9a-fA-F]{24}$/.test(id);
};

const getEntry = (modelType: ModelType, idModel: string): SubscriptionType => {
  switch (modelType) {
    case 'Member':
      return {
        idModel,
        modelType,
        tags: [Tags.Messages, Tags.Updates],
      };
    case 'Board':
      return {
        idModel,
        modelType,
        tags: [Tags.ClientActions, Tags.Updates],
      };
    case 'Organization':
      return {
        idModel,
        modelType,
        tags: [Tags.AllActions, Tags.Updates],
      };
    case 'Enterprise':
      if (
        featureFlagClient.get(
          'emerald-enable-enterprise-web-socket-subscription',
          false,
        )
      ) {
        return {
          idModel,
          modelType,
          tags: [Tags.AllActions, Tags.Updates],
        };
      } else {
        throw new Error('Invalid modelType');
      }
    default:
      throw new Error('Invalid modelType');
  }
};

export const useSocketSubscription = (
  modelType: ModelType,
  idModel?: string,
  skip?: boolean,
) => {
  useEffect(() => {
    if (!skip) {
      if (idModel && isMongoId(idModel)) {
        const unsubscribe = subscriber.addSubscription(
          getEntry(modelType, idModel),
        );
        return unsubscribe;
      } else {
        sendErrorEvent(
          new Error(
            `Cannot setup subscription to model '${modelType}' using an empty or invalid id: ${idModel}.`,
          ),
        );
      }
    }

    return () => {};
  }, [modelType, idModel, skip]);
};
